import React, { useState } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { QuickStartForm } from '../components/QuickStartForm';
import { ResultsDashboard } from '../components/ResultsDashboardV2';
import { BarChart3, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Footer } from '../components/legal';
import { Navigation } from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinancialApp = () => {
  const navigate = useNavigate();
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastInputData, setLastInputData] = useState(null);

  const handleCalculate = async (inputData) => {
    setLoading(true);
    setError(null);
    setLastInputData(inputData);

    try {
      const response = await axios.post(`${API}/analysis/calculate`, inputData);
      setAnalysisResult(response.data);
    } catch (err) {
      console.error('Calculation error:', err);
      setError('Fehler bei der Berechnung. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAnalysisResult(null);
    setError(null);
    setLastInputData(null);
  };

  return (
    <AppLayout>
      {/* Navigation */}
      <Navigation variant="light" />

      {/* Main Content - Input and Results on same page */}
      <div className="min-h-screen bg-gradient-to-b from-ice via-white to-ice pt-16 sm:pt-20">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-12 pb-4 sm:pb-8 text-center">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald/10 rounded-full text-emerald text-[10px] sm:text-xs font-medium uppercase tracking-wider mb-3 sm:mb-4">
            <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            Interaktive Analyse
          </div>
          <h1 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-2 sm:mb-3">
            Ihre persönliche Finanzanalyse
          </h1>
          <p className="text-sm sm:text-base text-charcoal max-w-2xl mx-auto px-2">
            Berechnen Sie Ihre Rentenlücke, vergleichen Sie Strategien und simulieren Sie Ihre Vermögensentwicklung.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4 sm:mb-6">
            <div className="bg-orange/10 border border-orange/20 text-orange px-4 sm:px-6 py-3 sm:py-4 rounded-xl flex items-center gap-2 sm:gap-3" data-testid="error-message">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-orange/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-orange font-bold text-sm">!</span>
              </div>
              <span className="text-charcoal text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Input Form - Always visible */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-12">
          <QuickStartForm 
            onCalculate={handleCalculate} 
            loading={loading}
            initialData={lastInputData}
            showAsUpdate={!!analysisResult}
          />
        </div>

        {/* Results Dashboard - Appears below form */}
        {analysisResult && (
          <div className="animate-fade-in">
            <ResultsDashboard 
              data={analysisResult} 
              onReset={handleReset}
              onRecalculate={handleCalculate}
              inputData={lastInputData}
            />
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer variant="light" />
    </AppLayout>
  );
};

export default FinancialApp;
